// components/FreeConversion.js
// "Upload Your Old Proove Results" — free, once per report type, forever,
// per account (backend: POST /typed_report/free_conversion). This screen
// didn't exist before: the backend endpoint was fully built but nothing in
// the frontend ever called it. Renders the result through the exact same
// ReportShell-based templates used everywhere else (OpioidRiskReport /
// PainPerceptionReport / PgxReport), so a converted old report looks
// identical in structure to a freshly-analyzed one.
import React, { useContext, useEffect, useState } from "react";
import {
  Box, Typography, Stack, Button, CircularProgress, Alert, Radio,
} from "@mui/material";
import { ArrowBack, UploadFile, CheckCircle, HistoryToggleOff, Medication, Bolt, Science } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import UserContext from "./UserContext";
import ThemeToggle from "./ThemeToggle";
import NavUserMenu from "./NavUserMenu";
import OpioidRiskReport from "./reports/OpioidRiskReport";
import PainPerceptionReport from "./reports/PainPerceptionReport";
import PgxReport from "./reports/PgxReport";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#0a0e27", navyDark: "#0f1233", accent: "#637cdb" };

const REPORT_COMPONENTS = { opioid_risk: OpioidRiskReport, pain_perception: PainPerceptionReport, pgx: PgxReport };

const TYPES = [
  { value: "opioid_risk", label: "Opioid Risk", blurb: "Your old Proove Opioid Risk Profile", icon: Medication },
  { value: "pain_perception", label: "Pain Perception", blurb: "Your old Proove Pain Perception Profile", icon: Bolt },
  { value: "pgx", label: "Drug Metabolism", blurb: "Your old Proove Drug Metabolism Profile", icon: Science },
];

export default function FreeConversion() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const token = user?.token;

  const bg = isDark ? "#080d1a" : "#f0f4ff";
  const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.1)";
  const textSub = isDark ? "#94a3b8" : "#64748b";

  const [statusLoading, setStatusLoading] = useState(true);
  const [used, setUsed] = useState({});
  const [statusError, setStatusError] = useState("");

  const [reportType, setReportType] = useState("");
  const [files, setFiles] = useState([]);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      setStatusLoading(true);
      setStatusError("");
      try {
        const res = await axios.get(`${API_BASE}/typed_report/free_conversion_status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsed(res.data?.used || {});
      } catch (err) {
        setStatusError(err.response?.data?.detail || "Failed to load your conversion status. Please try again.");
      } finally {
        setStatusLoading(false);
      }
    })();
  }, [token]);

  const handleConvert = async () => {
    if (!reportType || !files.length) return;
    setConverting(true);
    setConvertError("");
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append("files", f));
      formData.append("report_type", reportType);
      formData.append("language", "English");
      const res = await axios.post(`${API_BASE}/typed_report/free_conversion`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(res.data);
      setUsed((prev) => ({ ...prev, [reportType]: true }));
    } catch (err) {
      setConvertError(err.response?.data?.detail || err.message || "Conversion failed. Please try again.");
    } finally {
      setConverting(false);
    }
  };

  const ResultComponent = result ? REPORT_COMPONENTS[result.report_type] : null;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg, display: "flex", flexDirection: "column" }}>
      <Box sx={{
        bgcolor: isDark ? "rgba(2,6,15,0.92)" : "rgba(0,37,96,0.97)",
        backdropFilter: "blur(16px) saturate(1.4)",
        borderBottom: "2px solid rgba(99,124,219,0.3)",
      }}>
        <Box sx={{ px: 3, height: 64, display: "flex", alignItems: "center", justifyContent: "space-between", maxWidth: 1100, mx: "auto" }}>
          <Box component={Link} to="/home"
            sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
            <img src="/logo-light.png" alt="Proove Genomics" style={{ height: 20, width: "auto" }} />
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ThemeToggle />
            <Box onClick={() => navigate("/home")} sx={{
              display: "flex", alignItems: "center", gap: 0.75,
              color: "rgba(255,255,255,0.6)", cursor: "pointer", fontSize: 12, fontWeight: 600,
              px: 1.25, py: 0.5, borderRadius: "8px",
              "&:hover": { color: BRAND.accent, bgcolor: "rgba(99,124,219,0.1)" },
            }}>
              <ArrowBack sx={{ fontSize: 15 }} />
              <Box sx={{ display: { xs: "none", sm: "block" } }}>Home</Box>
            </Box>
            <NavUserMenu />
          </Stack>
        </Box>
      </Box>

      <Box sx={{ flex: 1, maxWidth: result ? 900 : 640, mx: "auto", width: "100%", px: { xs: 2, md: 4 }, py: 5 }}>
        {!result && (
          <>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
              <UploadFile sx={{ color: BRAND.accent, fontSize: 26 }} />
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy }}>
                Upgrade Your Old Proove Report
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: 13.5, color: textSub, mb: 3 }}>
              Free, one-time per report type. Upload your old (2009–2017) Proove Genomics report and AIden will
              convert it into today's format — same structure, same clinical summary style as every other Proove
              AI report. This only works for genuine Proove reports; other labs' results use the paid upload option.
            </Typography>

            {statusError && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{statusError}</Alert>}

            {statusLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={26} sx={{ color: BRAND.navy }} />
              </Box>
            ) : (
              <>
                <Stack spacing={1.5} sx={{ mb: 3 }}>
                  {TYPES.map((t) => {
                    const isUsed = Boolean(used[t.value]);
                    const selected = reportType === t.value;
                    return (
                      <Box
                        key={t.value}
                        onClick={() => !isUsed && setReportType(t.value)}
                        sx={{
                          display: "flex", alignItems: "center", gap: 1.5,
                          p: 2, borderRadius: "12px", cursor: isUsed ? "default" : "pointer",
                          border: `1.5px solid ${selected ? BRAND.accent : border}`,
                          bgcolor: isUsed ? (isDark ? "rgba(255,255,255,0.02)" : "#f4f6fb")
                            : (isDark ? "rgba(255,255,255,0.03)" : "#fff"),
                          opacity: isUsed ? 0.6 : 1,
                        }}
                      >
                        <Box sx={{
                          width: 38, height: 38, borderRadius: "10px", flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          background: isUsed
                            ? "linear-gradient(135deg, rgba(34,197,94,0.15), rgba(34,197,94,0.06))"
                            : `linear-gradient(135deg, ${BRAND.navy}, #28336b)`,
                        }}>
                          <t.icon sx={{ fontSize: 18, color: isUsed ? "#22c55e" : BRAND.accent }} />
                        </Box>
                        <Radio checked={selected} disabled={isUsed} size="small"
                          sx={{ color: BRAND.accent, "&.Mui-checked": { color: BRAND.accent }, p: 0.5 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: isDark ? "#f1f5f9" : BRAND.navy }}>
                            {t.label}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: textSub }}>{t.blurb}</Typography>
                        </Box>
                        {isUsed && (
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <CheckCircle sx={{ fontSize: 15, color: "#22c55e" }} />
                            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#22c55e" }}>
                              Already converted
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                    );
                  })}
                </Stack>

                {reportType && (
                  <Box sx={{
                    p: 2.5, borderRadius: "12px", border: `1px dashed ${border}`,
                    bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#fff", mb: 3,
                  }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 1 }}>
                      Upload your old report (PDF or images)
                    </Typography>
                    <Button component="label" variant="outlined" fullWidth
                      sx={{ py: 1.5, textTransform: "none", borderRadius: "10px" }}>
                      {files.length ? `${files.length} file${files.length > 1 ? "s" : ""} selected` : "Choose file(s)…"}
                      <input type="file" accept="application/pdf,image/*" multiple hidden
                        onChange={(e) => setFiles(Array.from(e.target.files || []))} />
                    </Button>
                  </Box>
                )}

                {convertError && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{convertError}</Alert>}

                <Button
                  fullWidth variant="contained" disabled={!reportType || !files.length || converting}
                  onClick={handleConvert}
                  sx={{
                    py: 1.4, fontWeight: 700, textTransform: "none", borderRadius: "10px",
                    background: `linear-gradient(135deg, ${BRAND.navy}, #3F6AC0)`,
                    "&:hover": { background: `linear-gradient(135deg, ${BRAND.navyDark}, ${BRAND.navy})` },
                  }}
                >
                  {converting ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Convert My Report"}
                </Button>

                <Box
                  onClick={() => navigate("/my-reports")}
                  sx={{
                    display: "flex", alignItems: "center", gap: 0.75, mt: 3, cursor: "pointer",
                    color: textSub, fontSize: 12.5, fontWeight: 600, width: "fit-content",
                    "&:hover": { color: BRAND.accent },
                  }}
                >
                  <HistoryToggleOff sx={{ fontSize: 15 }} />
                  View previously converted reports in your Report Dashboard
                </Box>
              </>
            )}
          </>
        )}

        {result && ResultComponent && (
          <Box>
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              Converted and saved to your profile — this is now your current {TYPES.find((t) => t.value === result.report_type)?.label} report.
            </Alert>
            <ResultComponent
              extracted={result.extracted}
              clinicalSummaryHtml={result.clinical_summary_html}
              disclaimer={result.disclaimer}
              reportDate={new Date().toLocaleDateString()}
              variant="official"
            />
            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <Button variant="outlined" onClick={() => { setResult(null); setReportType(""); setFiles([]); }}
                sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 700 }}>
                Convert another report type
              </Button>
              <Button variant="contained" onClick={() => navigate("/my-reports")}
                sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 700, bgcolor: BRAND.navy }}>
                View Report Dashboard
              </Button>
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
}
